import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TestTube, Play, CheckCircle2, XCircle, Clock, AlertTriangle, Loader2,
  Mail, Brain, Send, Filter, Database, Calendar, Zap, Code, Settings,
  ChevronRight, ChevronDown, Copy, Download, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { workflowExecutionEngine } from '@/services/workflowExecutionEngine';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface NodeTest {
  id: string;
  category: 'trigger' | 'ai' | 'action' | 'condition' | 'control';
  name: string;
  description: string;
  icon: any;
  testConfig: any;
  expectedOutput: any;
  status: 'pending' | 'running' | 'success' | 'error';
  result?: any;
  executionTime?: number;
  errorMessage?: string;
}

const nodeTests: NodeTest[] = [
  // Trigger Nodes
  {
    id: 'trigger-email',
    category: 'trigger',
    name: 'Email Received Trigger',
    description: 'Test email trigger with sample data',
    icon: Mail,
    testConfig: {
      triggerType: 'email_received',
      data: {
        from: 'test@example.com',
        subject: 'Test Email',
        body: 'This is a test email',
        isUrgent: true
      }
    },
    expectedOutput: { from: 'test@example.com' },
    status: 'pending'
  },
  
  // AI Nodes
  {
    id: 'ai-classify',
    category: 'ai',
    name: 'AI Classify',
    description: 'Test email classification with GPT-5',
    icon: Brain,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Email Received',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'ai-1',
          type: 'ai',
          title: 'AI Classify',
          description: 'Classify email',
          config: {
            prompt: 'Classify this email: Subject: Urgent Meeting | Body: We need to meet tomorrow'
          }
        }
      ]
    },
    expectedOutput: { category: 'work', priority: 'high' },
    status: 'pending'
  },
  {
    id: 'ai-extract',
    category: 'ai',
    name: 'AI Extract',
    description: 'Test entity extraction',
    icon: Brain,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Email Received',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'ai-1',
          type: 'ai',
          title: 'AI Extract',
          description: 'Extract entities',
          config: {
            prompt: 'Extract entities from: Meeting with John Doe at Acme Corp on Friday at 2pm in Conference Room A'
          }
        }
      ]
    },
    expectedOutput: { entities: [{ type: 'person', value: 'John Doe' }] },
    status: 'pending'
  },
  {
    id: 'ai-generate',
    category: 'ai',
    name: 'AI Generate',
    description: 'Test content generation',
    icon: Brain,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Email Received',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'ai-1',
          type: 'ai',
          title: 'AI Generate',
          description: 'Generate reply',
          config: {
            prompt: 'Generate a professional email reply thanking someone for their inquiry',
            maxTokens: 200
          }
        }
      ]
    },
    expectedOutput: { generatedText: 'Thank you' },
    status: 'pending'
  },
  {
    id: 'ai-research',
    category: 'ai',
    name: 'AI Research',
    description: 'Test web research capability',
    icon: Brain,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Email Received',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'ai-1',
          type: 'ai',
          title: 'AI Research',
          description: 'Research topic',
          config: {
            prompt: 'Research the latest AI trends in 2024',
            maxTokens: 500
          }
        }
      ]
    },
    expectedOutput: { research: 'AI trends', sources: [] },
    status: 'pending'
  },
  {
    id: 'ai-analyze',
    category: 'ai',
    name: 'AI Analyze',
    description: 'Test analysis with insights',
    icon: Brain,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Email Received',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'ai-1',
          type: 'ai',
          title: 'AI Analyze',
          description: 'Analyze data',
          config: {
            prompt: 'Analyze this email thread for sentiment and importance'
          }
        }
      ]
    },
    expectedOutput: { analysis: 'Analysis', insights: [] },
    status: 'pending'
  },

  // Action Nodes
  {
    id: 'action-log',
    category: 'action',
    name: 'Log Action',
    description: 'Test simple logging action',
    icon: Code,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Manual Trigger',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'action-1',
          type: 'action',
          title: 'log',
          description: 'Log message',
          config: {
            message: 'Test log message from workflow'
          }
        }
      ]
    },
    expectedOutput: { success: true, logged: true },
    status: 'pending'
  },

  // Condition Nodes
  {
    id: 'condition-branch',
    category: 'condition',
    name: 'Conditional Branch',
    description: 'Test condition evaluation',
    icon: Filter,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Manual Trigger',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'condition-1',
          type: 'action',
          title: 'Conditional Branch',
          description: 'Check condition',
          config: {
            condition: {
              field: 'trigger.isUrgent',
              operator: 'equals',
              value: true
            }
          }
        }
      ]
    },
    expectedOutput: { conditionMet: true },
    status: 'pending'
  },

  // Control Flow
  {
    id: 'control-wait',
    category: 'control',
    name: 'Wait Action',
    description: 'Test delay execution',
    icon: Clock,
    testConfig: {
      nodes: [
        {
          id: 'trigger-1',
          type: 'trigger',
          title: 'Manual Trigger',
          description: 'Test trigger',
          config: { triggerType: 'manual' }
        },
        {
          id: 'action-1',
          type: 'action',
          title: 'Wait',
          description: 'Wait 500ms',
          config: {
            delay: 500
          }
        }
      ]
    },
    expectedOutput: { success: true, waited: 500 },
    status: 'pending'
  }
];

export function NodeTestDashboard() {
  const [tests, setTests] = useState<NodeTest[]>(nodeTests);
  const [selectedTest, setSelectedTest] = useState<NodeTest | null>(null);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [progress, setProgress] = useState(0);
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());

  const categoryIcons = {
    trigger: Mail,
    ai: Brain,
    action: Send,
    condition: Filter,
    control: Settings
  };

  const runTest = async (test: NodeTest) => {
    const startTime = Date.now();
    
    setTests(prev => prev.map(t => 
      t.id === test.id ? { ...t, status: 'running' as const } : t
    ));

    try {
      const context = {
        userId: 'test-user-123',
        trigger: test.testConfig.data || {
          from: 'test@example.com',
          subject: 'Test',
          body: 'Test body',
          isUrgent: true
        }
      };

      const workflow = {
        id: `test-${test.id}`,
        name: test.name,
        description: test.description,
        nodes: test.testConfig.nodes || [],
        enabled: true,
        userId: 'test-user-123'
      };

      console.log(`🧪 Running test: ${test.name}`, { workflow, context });

      const result = await workflowExecutionEngine.executeWorkflow(workflow, context);
      const executionTime = Date.now() - startTime;

      console.log(`✅ Test completed: ${test.name}`, result);

      setTests(prev => prev.map(t =>
        t.id === test.id
          ? {
              ...t,
              status: 'success' as const,
              result,
              executionTime
            }
          : t
      ));

      toast.success(`Test passed: ${test.name}`, {
        description: `Completed in ${executionTime}ms`
      });
    } catch (error) {
      const executionTime = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      console.error(`❌ Test failed: ${test.name}`, error);

      setTests(prev => prev.map(t =>
        t.id === test.id
          ? {
              ...t,
              status: 'error' as const,
              errorMessage,
              executionTime
            }
          : t
      ));

      toast.error(`Test failed: ${test.name}`, {
        description: errorMessage
      });
    }
  };

  const runAllTests = async () => {
    setIsRunningAll(true);
    setProgress(0);

    for (let i = 0; i < tests.length; i++) {
      await runTest(tests[i]);
      setProgress(((i + 1) / tests.length) * 100);
      
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsRunningAll(false);

    const passed = tests.filter(t => t.status === 'success').length;
    const failed = tests.filter(t => t.status === 'error').length;

    toast.success('All tests completed', {
      description: `${passed} passed, ${failed} failed`
    });
  };

  const resetTests = () => {
    setTests(prev => prev.map(t => ({
      ...t,
      status: 'pending' as const,
      result: undefined,
      errorMessage: undefined,
      executionTime: undefined
    })));
    setProgress(0);
    toast.info('Tests reset');
  };

  const toggleExpanded = (testId: string) => {
    setExpandedTests(prev => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  const getStatusIcon = (status: NodeTest['status']) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const copyResult = (test: NodeTest) => {
    const data = {
      test: test.name,
      status: test.status,
      executionTime: test.executionTime,
      result: test.result,
      error: test.errorMessage
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success('Result copied to clipboard');
  };

  const exportResults = () => {
    const results = tests.map(t => ({
      id: t.id,
      name: t.name,
      category: t.category,
      status: t.status,
      executionTime: t.executionTime,
      result: t.result,
      error: t.errorMessage
    }));
    
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `node-test-results-${Date.now()}.json`;
    a.click();
    
    toast.success('Results exported');
  };

  const stats = {
    total: tests.length,
    passed: tests.filter(t => t.status === 'success').length,
    failed: tests.filter(t => t.status === 'error').length,
    pending: tests.filter(t => t.status === 'pending').length,
    running: tests.filter(t => t.status === 'running').length
  };

  const categories = ['trigger', 'ai', 'action', 'condition', 'control'] as const;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 glass rounded-lg">
              <TestTube className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Workflow Node Test Dashboard</h1>
              <p className="text-muted-foreground">
                Live testing suite for all workflow node types
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={resetTests}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button variant="outline" onClick={exportResults}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button onClick={runAllTests} disabled={isRunningAll}>
              {isRunningAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Run All Tests
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Total</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </Card>
          <Card className="p-4 border-green-500/20">
            <div className="text-sm text-muted-foreground mb-1">Passed</div>
            <div className="text-2xl font-bold text-green-500">{stats.passed}</div>
          </Card>
          <Card className="p-4 border-red-500/20">
            <div className="text-sm text-muted-foreground mb-1">Failed</div>
            <div className="text-2xl font-bold text-red-500">{stats.failed}</div>
          </Card>
          <Card className="p-4 border-blue-500/20">
            <div className="text-sm text-muted-foreground mb-1">Running</div>
            <div className="text-2xl font-bold text-blue-500">{stats.running}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Pending</div>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </Card>
        </div>

        {isRunningAll && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Overall Progress</span>
              <span className="text-sm font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
      </div>

      {/* Test Categories */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Tests</TabsTrigger>
          {categories.map(cat => {
            const Icon = categoryIcons[cat];
            const count = tests.filter(t => t.category === cat).length;
            return (
              <TabsTrigger key={cat} value={cat} className="gap-2">
                <Icon className="w-4 h-4" />
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
                <Badge variant="secondary" className="text-xs">{count}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {['all', ...categories].map(category => {
          const filteredTests = category === 'all' 
            ? tests 
            : tests.filter(t => t.category === category);

          return (
            <TabsContent key={category} value={category} className="space-y-2">
              {filteredTests.map(test => {
                const isExpanded = expandedTests.has(test.id);
                const Icon = test.icon;

                return (
                  <motion.div
                    key={test.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 glass rounded-lg">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{test.name}</h3>
                          {getStatusIcon(test.status)}
                          <Badge variant="outline" className="text-xs">
                            {test.category}
                          </Badge>
                          {test.executionTime && (
                            <Badge variant="secondary" className="text-xs">
                              {test.executionTime}ms
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{test.description}</p>

                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-4 space-y-3"
                          >
                            {/* Test Config */}
                            <div>
                              <h4 className="text-xs font-semibold mb-2">Test Configuration</h4>
                              <Textarea
                                value={JSON.stringify(test.testConfig, null, 2)}
                                readOnly
                                className="font-mono text-xs min-h-[100px]"
                              />
                            </div>

                            {/* Result */}
                            {test.result && (
                              <div>
                                <h4 className="text-xs font-semibold mb-2">Result</h4>
                                <Textarea
                                  value={JSON.stringify(test.result, null, 2)}
                                  readOnly
                                  className="font-mono text-xs min-h-[100px] bg-green-500/5"
                                />
                              </div>
                            )}

                            {/* Error */}
                            {test.errorMessage && (
                              <div>
                                <h4 className="text-xs font-semibold mb-2 text-red-500">Error</h4>
                                <Textarea
                                  value={test.errorMessage}
                                  readOnly
                                  className="font-mono text-xs min-h-[60px] bg-red-500/5 text-red-500"
                                />
                              </div>
                            )}

                            {/* Expected Output */}
                            <div>
                              <h4 className="text-xs font-semibold mb-2">Expected Output</h4>
                              <Textarea
                                value={JSON.stringify(test.expectedOutput, null, 2)}
                                readOnly
                                className="font-mono text-xs min-h-[60px] bg-accent/50"
                              />
                            </div>
                          </motion.div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(test.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                        {test.result && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyResult(test)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => runTest(test)}
                          disabled={test.status === 'running'}
                        >
                          {test.status === 'running' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

export default NodeTestDashboard;
